import { useEffect, useState } from "react";
import "@/App.css";
import { AnimatePresence } from "framer-motion";
import { Toaster } from "@/components/ui/sonner";
import { Mail } from "lucide-react";
import EnvelopeOverlay from "@/components/wedding/EnvelopeOverlay";
import HeaderSection from "@/components/wedding/HeaderSection";
import InvitationText from "@/components/wedding/InvitationText";
import CouplePhoto from "@/components/wedding/CouplePhoto";
import VinylPlayer from "@/components/wedding/VinylPlayer";
import EventDetails from "@/components/wedding/EventDetails";
import DateStrip from "@/components/wedding/DateStrip";
import DayProgram from "@/components/wedding/DayProgram";
import RsvpForm from "@/components/wedding/RsvpForm";
import CalendarSection from "@/components/wedding/CalendarSection";
import { GoldDivider, CornerFlourish, SectionDivider } from "@/components/wedding/Ornaments";

const DEFAULT_DETAILS = {
  name1: "ნიკოლოზი",
  name2: "მარიამი",
  date: "17 ნოემბერი, 2026",
};

const FadeIn = ({ children }) => <>{children}</>;

function App() {
  const [envelopeVisible, setEnvelopeVisible] = useState(true);
  const [hasOpened, setHasOpened] = useState(false);
  const [details, setDetails] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("wedding-details-nm-v2")) || DEFAULT_DETAILS;
    } catch {
      return DEFAULT_DETAILS;
    }
  });

  useEffect(() => {
    const html = document.documentElement;
    if (envelopeVisible) {
      html.style.overflow = "hidden";
      html.style.overscrollBehavior = "none";
      document.body.style.overflow = "hidden";
      document.body.style.overscrollBehavior = "none";
    } else {
      html.style.overflow = "";
      html.style.overscrollBehavior = "";
      document.body.style.overflow = "";
      document.body.style.overscrollBehavior = "";
    }
    return () => {
      html.style.overflow = "";
      html.style.overscrollBehavior = "";
      document.body.style.overflow = "";
      document.body.style.overscrollBehavior = "";
    };
  }, [envelopeVisible]);

  const saveDetails = (d) => {
    setDetails(d);
    localStorage.setItem("wedding-details-nm-v2", JSON.stringify(d));
  };

  const handleOpened = () => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    setHasOpened(true);
    setEnvelopeVisible(false);
  };

  return (
    <div className="min-h-screen paper-bg text-[#1C1917]">
      <Toaster position="top-center" />

      <div className="glow-top" aria-hidden="true" />
      <div className="glow-bottom" aria-hidden="true" />

      <AnimatePresence>
        {envelopeVisible && (
          <EnvelopeOverlay details={details} onOpened={handleOpened} />
        )}
      </AnimatePresence>

      {hasOpened && (
        <button
          data-testid="envelope-reopen-button"
          onClick={() => setEnvelopeVisible(true)}
          title="კონვერტის ხელახლა გახსნა"
          aria-label="კონვერტის ხელახლა გახსნა"
          className="fixed bottom-5 right-5 z-40 flex h-11 w-11 items-center justify-center rounded-full border border-[#E5D5B8] bg-white/90 text-[#B8860B] shadow-lg backdrop-blur transition-transform hover:scale-110 active:scale-95"
        >
          <Mail size={17} />
        </button>
      )}

      <main
        className={`relative z-[1] mx-auto max-w-md px-4 py-8 sm:max-w-lg sm:py-12 ${hasOpened ? "" : "invisible"}`}
        aria-hidden={!hasOpened}
      >
        <div className="invite-frame space-y-12 px-6 py-12 sm:px-10">
          <CornerFlourish className="!mt-0 absolute left-2 top-2" />
          <CornerFlourish className="!mt-0 absolute right-2 top-2 -scale-x-100" />
          <CornerFlourish className="!mt-0 absolute bottom-2 right-2 -scale-x-100 -scale-y-100" />
          <CornerFlourish className="!mt-0 absolute bottom-2 left-2 -scale-y-100" />

          <FadeIn>
            <HeaderSection details={details} onSave={saveDetails} />
          </FadeIn>

          <SectionDivider testid="section-divider-1" />

          <FadeIn>
            <InvitationText details={details} />
          </FadeIn>

          <SectionDivider testid="section-divider-2" />

          <FadeIn>
            <CouplePhoto details={details} />
          </FadeIn>

          <SectionDivider testid="section-divider-3" />

          <FadeIn>
            <VinylPlayer />
          </FadeIn>

          <SectionDivider testid="section-divider-4" />

          <FadeIn>
            <EventDetails details={details} />
          </FadeIn>

          <FadeIn>
            <DateStrip />
          </FadeIn>

          <SectionDivider testid="section-divider-5" />

          <FadeIn>
            <DayProgram />
          </FadeIn>

          <SectionDivider testid="section-divider-6" />

          <FadeIn>
            <RsvpForm />
          </FadeIn>

          <SectionDivider testid="section-divider-7" />

          <FadeIn>
            <CalendarSection />
          </FadeIn>

          <FadeIn>
            <footer className="pt-2 text-center">
              <GoldDivider testid="footer-ornate-divider" />
              <p className="mt-5 text-[11px] uppercase tracking-[0.25em] text-[#A1A1AA]">
                {details.name1} & {details.name2} · {details.date}
              </p>
            </footer>
          </FadeIn>
        </div>
      </main>
    </div>
  );
}

export default App;
